import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ActivityEvent,
} from "../../../otc-requests/types";
import { TransactionActivityLogEvent, TransactionItem } from "../../types";
import { mapTransactionToOtcRequest } from "./mapTransactionToOtcRequest";
import {
  approveOtcOffer,
  cancelOtcRequest,
  escalateOtcRequest,
  rejectOtcRequest,
  sendOtcOffer,
  updateOtcTransactionStatus,
  uploadReceiptFile,
  UpdateTransactionStatusPayload,
} from "../../../otc-requests/services";
import { SendOfferSubmitData } from "../../../otc-requests/components/SendOfferModal";
import { UpdateStatusSubmitData } from "../../../otc-requests/components/UpdateStatusModal";
import { getApiErrorMessage } from "../../../../shared/lib";
import { useParams } from "react-router-dom";
import { PAGES } from "../../../../shared/lib/pageUrlConstants";

export const useOtcDetailsState = (transaction: TransactionItem, id?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: trnxId } = useParams()

  // Pure live state derived directly from the backend transaction fetched by useQuery
  const currentRequest = useMemo(
    () => mapTransactionToOtcRequest(transaction, id ?? trnxId),
    [transaction, id, trnxId]
  );

  // Helper to re-fetch the latest transaction details from server after any mutation
  const invalidateTransactionQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ["admin-transaction-details"],
    });
  };

  // Modal open states
  const [isSendOfferOpen, setIsSendOfferOpen] = useState(false);
  const [isEscalateOpen, setIsEscalateOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isCancelOfferOpen, setIsCancelOfferOpen] = useState(false);
  const [isActionSubmitting, setIsActionSubmitting] = useState(false);

  const handleBack = () => {
    navigate(PAGES.transactions);
  };

  const handleSendOfferSubmit = async (offerData: SendOfferSubmitData) => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      await sendOtcOffer(targetId, offerData);
      setIsSendOfferOpen(false);
      toast.success("Offer sent to customer successfully");
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to send counter-offer"));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleApproveConfirm = async (
    providerId: string,
    note?: string,
    validity?: {
      validForValue: number;
      validForUnit: string;
      validityInMinutes?: number;
      expiringDate: string;
      expiringTime: string;
    }
  ) => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      await approveOtcOffer(targetId, {
        acceptRequestedRate: true,
        providerId,
        expiresInMinutes: validity?.validityInMinutes ?? 30,
      });

      setIsApproveOpen(false);
      toast.success("OTC request approved successfully");
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to approve OTC request"));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleCancelOffer = async () => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      await cancelOtcRequest(targetId, "Offer cancelled by admin");
      setIsCancelOfferOpen(false);
      toast.success("Offer cancelled successfully");
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to cancel offer"));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleUpdateStatusConfirm = async (data: UpdateStatusSubmitData) => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      let receiptUrl: string | undefined = undefined;
      if (data.status === "Completed" && data.receiptFile) {
        receiptUrl = await uploadReceiptFile(data.receiptFile);
      }

      const txPayload: UpdateTransactionStatusPayload =
        data.status === "Completed"
          ? receiptUrl
            ? { status: "Success", url: receiptUrl }
            : { status: "Success" }
          : data.status === "Failed"
            ? {
                status: "Failed",
                reason: data.reason || "Settlement could not be completed.",
              }
            : { status: "Processing" };

      await updateOtcTransactionStatus(targetId, txPayload);
      setIsUpdateStatusOpen(false);
      toast.success(`Transaction status updated to ${data.status}`);
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          `Failed to update transaction status to ${data.status}`
        )
      );
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleEscalateConfirm = async (data: {
    adminId: string;
    role: string;
    note: string;
  }) => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      await escalateOtcRequest(targetId, {
        adminId: data.adminId,
        role: data.role,
        note: data.note,
      });

      setIsEscalateOpen(false);
      toast.success(`Request escalated to ${data.role}`);
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to escalate request"));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    const targetId = currentRequest.id || currentRequest.transactionId;

    setIsActionSubmitting(true);
    try {
      await rejectOtcRequest(targetId, reason);
      setIsRejectOpen(false);
      toast.success("OTC request rejected successfully");
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to reject OTC request"));
    } finally {
      setIsActionSubmitting(false);
    }
  };

  const handleUpdateTransactionStatus = async (
    payload: UpdateTransactionStatusPayload
  ) => {
    const targetId = currentRequest.id || currentRequest.transactionId;
    try {
      await updateOtcTransactionStatus(targetId, payload);
      invalidateTransactionQuery();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update transaction status"));
    }
  };

  const activityLogs: TransactionActivityLogEvent[] = useMemo(() => {
    if (!currentRequest?.activityLog || currentRequest.activityLog.length === 0) {
      return [];
    }
    return currentRequest.activityLog.map((log: ActivityEvent, idx: number) => ({
      id: log.id || `act-${idx}`,
      timestamp: log.timestamp || "Recently",
      actorName: log.user || "Admin",
      actionPrefix: log.action || "Updated",
      boldTarget: log.target || "OTC Trade",
      actionSuffix: log.toValue ? `to ${log.toValue}` : "",
    }));
  }, [currentRequest?.activityLog]);

  return {
    currentRequest,
    activityLogs,
    canGoPrevious: false,
    canGoNext: false,
    handleNavigateSibling: () => {},
    handleBack,
    isSendOfferOpen,
    setIsSendOfferOpen,
    isEscalateOpen,
    setIsEscalateOpen,
    isRejectOpen,
    setIsRejectOpen,
    isApproveOpen,
    setIsApproveOpen,
    isUpdateStatusOpen,
    setIsUpdateStatusOpen,
    isCancelOfferOpen,
    setIsCancelOfferOpen,
    handleSendOfferSubmit,
    handleApproveConfirm,
    handleCancelOffer,
    handleUpdateStatusConfirm,
    handleEscalateConfirm,
    handleRejectConfirm,
    handleUpdateTransactionStatus,
    isActionSubmitting,
  };
};
